import { createAsyncThunk } from "@reduxjs/toolkit";
import BusinessApprovalApiService, {
  isAccessDeniedError,
  toFriendlyApprovalError,
} from "../apiServices/BusinessApprovalApiService";
import AdminSubscriptionApiService, {
  toFriendlyAdminSubscriptionError,
} from "../apiServices/AdminSubscriptionApiService";
import AdminBusinessListApiService, {
  ExtendSubscriptionInput,
  toFriendlyAdminBusinessListError,
  toFriendlyExtendSubscriptionError,
} from "../apiServices/AdminBusinessListApiService";
import BusinessApiService from "../apiServices/BusinessApiService";
import {
  AdminPendingBusiness,
  BusinessDecisionInput,
} from "../../interfaces/AdminApprovalObject";
import { BusinessSubscription } from "../../interfaces/AdminSubscriptionObject";
import {
  AdminBusinessListItem,
  AdminBusinessListPage,
  AdminExtendSubscriptionResult,
  BUSINESS_LIST_PAGE_SIZE,
} from "../../interfaces/AdminBusinessListObject";
import { ApprovalStatus } from "../../interfaces/OnboardingObject";

// Thunks behind Admin -> Businesses. One file per admin SECTION (not per tab):
// the tabs of one section share a slice, so their thunks staying together keeps
// the import graph flat.
//
// Convention: every thunk rejects with a STRING already made friendly by its
// api service, so reducers can assign action.payload straight into errorMap.
// The one exception is the access-denied case, which rejects with the sentinel
// below so the slice can render "no access" as a STATE rather than an error -
// the same distinction OnboardingDb draws for CAPTAIN_NOT_FOUND.

export const ADMIN_ACCESS_DENIED = "ADMIN_ACCESS_DENIED";

// ── Approvals ───────────────────────────────────────────────────────────────

/** Loads one approval status. The status travels back in the fulfilled action
 *  so the reducer knows which per-status cache to fill without a closure. */
export const fetchAdminPendingBusinesses = createAsyncThunk<
  { status: ApprovalStatus; rows: AdminPendingBusiness[] },
  ApprovalStatus,
  { rejectValue: string }
>("admin/fetchPendingBusinesses", async (status, { rejectWithValue }) => {
  try {
    const rows = await BusinessApprovalApiService.listPendingBusinesses(status);
    return { status, rows };
  } catch (error) {
    if (isAccessDeniedError(error)) return rejectWithValue(ADMIN_ACCESS_DENIED);
    return rejectWithValue(toFriendlyApprovalError(error));
  }
});

/**
 * Approve or reject one submission.
 *
 * Returns both the updated row and the status it came FROM, because the
 * reducer has to move it between per-status caches and the row itself only
 * reports where it landed.
 */
export const decideBusiness = createAsyncThunk<
  { row: AdminPendingBusiness; from: ApprovalStatus },
  BusinessDecisionInput & { from?: ApprovalStatus },
  { rejectValue: string }
>("admin/decideBusiness", async (input, { rejectWithValue }) => {
  try {
    const row = await BusinessApprovalApiService.decideBusiness({
      pendingId: input.pendingId,
      decision: input.decision,
      rejectionReason: input.rejectionReason,
    });
    return { row, from: input.from ?? "PENDING" };
  } catch (error) {
    return rejectWithValue(toFriendlyApprovalError(error));
  }
});

export const markBusinessPaid = createAsyncThunk<
  { row: AdminPendingBusiness; from: ApprovalStatus },
  { business: AdminPendingBusiness; note: string },
  { rejectValue: string }
>("admin/markBusinessPaid", async (input, { rejectWithValue }) => {
  try {
    const patch = await BusinessApprovalApiService.markBusinessPaid(
      input.business.pendingId,
      input.note
    );
    // The API only returns partial updated fields for mark-paid (e.g. paymentStatus).
    // Merge them into the existing row so the UI store isn't overwritten with blanks.
    const row = { ...input.business, ...patch };
    return { row, from: input.business.approvalStatus };
  } catch (error) {
    return rejectWithValue(toFriendlyApprovalError(error));
  }
});

// ── Subscriptions ───────────────────────────────────────────────────────────

/** Loads business subscriptions for a status filter. The filter travels back
 *  in the fulfilled action so a stale response from a filter the user has
 *  already changed can be ignored by the reducer. */
export const fetchAdminBusinessSubscriptions = createAsyncThunk<
  { statuses: string[]; rows: BusinessSubscription[] },
  string[],
  { rejectValue: string }
>("admin/fetchBusinessSubscriptions", async (statuses, { rejectWithValue }) => {
  try {
    const rows = await AdminSubscriptionApiService.listBusinessSubscriptions(
      statuses
    );
    return { statuses, rows };
  } catch (error) {
    return rejectWithValue(toFriendlyAdminSubscriptionError(error));
  }
});

/** One unfiltered load of subscriptions across EVERY chip status, used only to
 *  put a count on each status chip (the main list stays filtered server-side,
 *  one status at a time). Rows are tallied client-side by `status`. */
export const fetchAdminSubscriptionCounts = createAsyncThunk<
  BusinessSubscription[],
  string[],
  { rejectValue: string }
>("admin/fetchBusinessSubscriptionCounts", async (allStatuses, { rejectWithValue }) => {
  try {
    return await AdminSubscriptionApiService.listBusinessSubscriptions(allStatuses);
  } catch (error) {
    return rejectWithValue(toFriendlyAdminSubscriptionError(error));
  }
});

/**
 * Resolves ONE businessId to a display name.
 *
 * A workaround, not the design: the subscriptions list identifies businesses by
 * uuid only, and a screen of uuids is not reviewable. Kept deliberately
 * single-id and fire-and-forget so the list renders immediately and names fill
 * in; the slice caches by id so a name is fetched at most once per session.
 * Delete this the day the list response carries `businessName`.
 */
export const fetchAdminBusinessName = createAsyncThunk<
  { businessId: string; name: string },
  string,
  { rejectValue: string }
>("admin/fetchBusinessName", async (businessId, { rejectWithValue }) => {
  try {
    // getBusinessById already unwraps the envelope and returns a converted
    // BusinessProfile - do not re-unwrap `contents` here.
    const business = await BusinessApiService.getBusinessById(businessId);
    const name = business?.name ?? "";
    if (!name) return rejectWithValue("NO_NAME");
    return { businessId, name };
  } catch (error) {
    // Never surfaced: a missing name degrades to showing the id.
    return rejectWithValue("NO_NAME");
  }
});

// ── All Businesses ─────────────────────────────────────────────────────────

export interface FetchAdminBusinessListArg {
  expired: boolean;
  page: number;
  /** true when this call is a "Load more" continuation - the reducer appends
   *  instead of replacing the bucket's rows. Defaults to false (a fresh
   *  fetch/refresh replaces). */
  append?: boolean;
}

/** Loads one page of one `expired` bucket. Both travel back in the fulfilled
 *  action (alongside the page result) so the reducer can target the right
 *  bucket and decide append-vs-replace without a closure. */
export const fetchAdminBusinessList = createAsyncThunk<
  { expired: boolean; append: boolean; result: AdminBusinessListPage },
  FetchAdminBusinessListArg,
  { rejectValue: string }
>("admin/fetchBusinessList", async ({ expired, page, append }, { rejectWithValue }) => {
  try {
    const result = await AdminBusinessListApiService.listBusinesses({
      expired,
      page,
      size: BUSINESS_LIST_PAGE_SIZE,
    });
    return { expired, append: !!append, result };
  } catch (error) {
    return rejectWithValue(toFriendlyAdminBusinessListError(error));
  }
});

/** Page size used ONLY by the no-subscription scan below - bigger than the
 *  normal list page so the scan needs as few round trips as possible. */
const NO_SUBSCRIPTION_SCAN_PAGE_SIZE = 100;
/** Hard stop on the scan (MAX_PAGES * page size rows) so a misbehaving
 *  totalPages can never loop forever. */
const NO_SUBSCRIPTION_SCAN_MAX_PAGES = 50;

/**
 * Every business that has never had a subscription (subscriptionEnd null).
 *
 * The list endpoint has no "no subscription" filter, and those rows come back
 * inside the non-expired (`expired` omitted) listing - so this walks every
 * non-expired page and keeps only the null-subscriptionEnd rows. Returns the
 * complete set in one go (no "Load more"), which is also what makes its count
 * accurate. Swap this for a server-side filter the day the backend adds one.
 */
export const fetchAdminNoSubscriptionBusinesses = createAsyncThunk<
  AdminBusinessListItem[],
  void,
  { rejectValue: string }
>("admin/fetchNoSubscriptionBusinesses", async (_, { rejectWithValue }) => {
  try {
    const rows: AdminBusinessListItem[] = [];
    let page = 0;
    let totalPages = 1;
    while (page < totalPages && page < NO_SUBSCRIPTION_SCAN_MAX_PAGES) {
      const result = await AdminBusinessListApiService.listBusinesses({
        expired: false,
        page,
        size: NO_SUBSCRIPTION_SCAN_PAGE_SIZE,
      });
      rows.push(...result.content.filter((row) => !row.subscriptionEnd));
      totalPages = result.totalPages;
      page += 1;
    }
    return rows;
  } catch (error) {
    return rejectWithValue(toFriendlyAdminBusinessListError(error));
  }
});

/**
 * Manually extends a business's subscription end date by `days`. Returns the
 * full result (previous/new end dates, subscriptionId) so the reducer can
 * patch `subscriptionEnd` in place on whichever bucket(s) hold this
 * businessId, without a refetch.
 */
export const extendBusinessSubscription = createAsyncThunk<
  AdminExtendSubscriptionResult,
  ExtendSubscriptionInput,
  { rejectValue: string }
>("admin/extendBusinessSubscription", async ({ businessId, days }, { rejectWithValue }) => {
  try {
    return await AdminBusinessListApiService.extendSubscription({ businessId, days });
  } catch (error) {
    return rejectWithValue(toFriendlyExtendSubscriptionError(error));
  }
});

const AdminBusinessesThunk = {
  fetchAdminPendingBusinesses,
  decideBusiness,
  fetchAdminBusinessSubscriptions,
  fetchAdminSubscriptionCounts,
  fetchAdminBusinessName,
  fetchAdminBusinessList,
  fetchAdminNoSubscriptionBusinesses,
  extendBusinessSubscription,
};

export default AdminBusinessesThunk;
